import React, { useEffect, useState } from "react";
import { Media } from "react-bootstrap";
import { BASE_URL } from "../helper/constants";
import { Skeleton } from "@material-ui/lab";
import LanguageContext from "../helper/LanguageContext";
import { convertBranchRole } from "../helper/convertor";
import PlayerFeed from "./PlayerFeed";
import ReadMores from "./ReadMores";
import MaterialIcon from "./MaterialIcon";
import { stripMarkdownFromTitle } from "../helper/stripMarkdownTitle";
import "./ArticleCard.css";

const extractUrls = require("extract-urls");

/**
 * Article card matching the feed design: author (avatar + name + role), title,
 * rich text description, image gallery, date, and optional like/follow actions.
 * Used in feed lists and create/edit preview.
 */
export default function ArticleCard({
  article,
  isInternal = false,
  viewArticleDetail,
  onLike,
  user,
  showActions = true,
  /** Preview mode: no click handlers, just visual */
  previewOnly = false,
}) {
  const { isGerm, strings } = React.useContext(LanguageContext);
  const [articleBranch, setArticleBranch] = useState("");
  const [_Link, setLink] = useState(null);

  useEffect(() => {
    setArticleBranch(convertBranchRole(article?.buildingIndustryRole, isGerm) || "");
  }, [article, isGerm]);

  useEffect(() => {
    if (!article) {
      setLink(null);
      return;
    }
    if (article?.description && typeof article?.description === "string") {
      let datax = extractUrls(article?.description);
      datax =
        datax && Array.isArray(datax)
          ? datax?.map((e) => e.replace("&quot", ""))
          : datax;
      let data = [...new Set(datax || [])];
      let all = [];
      if (data && data.length > 0) {
        for (let obj of data) {
          let x = { href: obj };
          if (obj.match("vimeo")) {
            x.videoType = "vimeo";
            all.push(x);
          }
          if (obj.match("youtube") || obj.match("youtu")) {
            x.videoType = "youtube";
            all.push(x);
          }
        }
        if (all.length > 0) {
          if (article.uploadUrl && article.uploadUrl.length > 0) {
            setLink([...all, ...article.uploadUrl]);
          } else {
            setLink([...all]);
          }
        } else if (article.uploadUrl && article.uploadUrl.length > 0) {
          setLink([...article.uploadUrl]);
        } else {
          setLink(null);
        }
      } else if (article.uploadUrl && article.uploadUrl.length > 0) {
        setLink([...article.uploadUrl]);
      } else {
        setLink(null);
      }
    } else if (article.uploadUrl && article.uploadUrl.length > 0) {
      setLink([...article.uploadUrl]);
    } else {
      setLink(null);
    }
  }, [article]);

  if (!article) {
    return (
      <div className="feed-details">
        <Skeleton variant="rect" height={120} />
      </div>
    );
  }

  const authorName = article.userFullName || article.postedByName || "";
  const isOwnPost = user && (article.postedById === user.id || article.userId === user.id);

  const avatarSrc = isInternal
    ? article.postedByImage
      ? BASE_URL + article.postedByImage
      : article.userAvatar
        ? BASE_URL + article.userAvatar
        : article.userImage
          ? BASE_URL + article.userImage
          : article.userProfileImage
            ? BASE_URL + article.userProfileImage
            : process.env.PUBLIC_URL + "assets/img/head.svg"
    : article.logo
      ? BASE_URL + article.logo
      : article.avatar
        ? BASE_URL + article.avatar
        : article.postedByImage
          ? BASE_URL + article.postedByImage
          : process.env.PUBLIC_URL + "/assets/img/Companies-No-Image.svg";

  const dateStr = article.publishedAt
    ? new Date(Number(article.publishedAt) * 1000).toLocaleDateString(isGerm ? "de-DE" : "en-GB", {
        day: "numeric",
        month: "long",
      }) +
      ", " +
      new Date(Number(article.publishedAt) * 1000).toLocaleTimeString(isGerm ? "de-DE" : "en-US", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : article.createdAt
      ? new Date(article.createdAt).toLocaleDateString(isGerm ? "de-DE" : "en-GB", {
          day: "numeric",
          month: "long",
        }) +
        ", " +
        new Date(article.createdAt).toLocaleTimeString(isGerm ? "de-DE" : "en-US", {
          hour: "2-digit",
          minute: "2-digit",
        })
      : "";

  return (
    <div className="feed-details">
      <Media className="article-author-media">
        <img
          width={isInternal ? 25 : 40}
          height={isInternal ? 25 : 40}
          className={`mr-3 news_profile article-card__avatar${isInternal ? " article-card__avatar--round" : ""}`}
          src={avatarSrc}
          alt=""
        />
        <Media.Body>
          <div className={isInternal ? "authore_name internal-article-author" : "authore_name"}>
            <h5>
              <span className="internal-article-name">{authorName}</span>
              {isInternal && articleBranch && (
                <span className="internal-article-branch" data-tooltip={articleBranch}>
                  - {articleBranch}
                </span>
              )}
            </h5>
          </div>
          {!isInternal && (
            <div className="w-100">
              {article.postedByName && (
                <h6>
                  <span className="text-73 article-card__poster">
                    {article.postedByName}
                  </span>{" "}
                  - <span>{articleBranch}</span>
                </h6>
              )}
              {article.product && (
                <h6 className="product_user">
                  Product: <span>{article.product}</span>
                </h6>
              )}
            </div>
          )}
        </Media.Body>
      </Media>
      {isInternal && article.deptLocation && Array.isArray(article.deptLocation) && article.deptLocation.filter(d => d && d.departmentLocation).length > 0 && (
        <div className="article-dept-location-tabs">
          {article.deptLocation.filter(d => d && d.departmentLocation).map((dept, i) => (
            <span key={dept.deptLocation || i} className="article-dept-location-tab">
              {dept.departmentLocation}
            </span>
          ))}
        </div>
      )}

      <p className="m-0 mt-2 mb-2 article-card__title">
        {stripMarkdownFromTitle(article.title || "")}
      </p>

      {article?.description ? (
        <div className="company-feed-image-scope">
          <div className="small article-card__desc">
            <ReadMores isInternal={isInternal}>{article?.description}</ReadMores>
          </div>
        </div>
      ) : null}

      {_Link && _Link.length > 0 && (
        <div className="company-feed-image-scope">
          <PlayerFeed
            Links={_Link}
            article={article}
            viewArticleDetail={viewArticleDetail}
            isInternal={isInternal}
          />
        </div>
      )}

      <div
        className={
          !isInternal
            ? "date-follow no-border noHover article-card__footer"
            : "date-follow no-border-internal noHover article-card__footer"
        }
      >
        <p className="m-0">{dateStr}</p>
        {showActions && !previewOnly && onLike && (
          <a
            onClick={(e) => !isOwnPost && onLike && onLike(article)}
            className={`date-follow-link article-card__like ${isOwnPost ? "date-follow-link-deactivated" : "pointer"}`}
          >
            <MaterialIcon
              icon="thumb_up"
              size={24}
              weight={300}
              fill={article.likeStatus ? 1 : 0}
              color={isOwnPost ? "#e0e0e0" : article.likeStatus ? (isInternal ? "var(--kjuup-internal)" : "var(--kjuup-accent)") : "#737373"}
              className="mr-1 article-card__like-icon"
            />
            <span className="date-follow-label article-card__like-label">
              {strings?.like || "Gefällt mir"}
            </span>
          </a>
        )}
        {previewOnly && (
          <div className="article-card__preview">
            <span className="date-follow-link article-card__preview-like">
              <MaterialIcon icon="thumb_up" size={24} weight={300} fill={0} color="#737373" className="mr-1 article-card__like-icon" />
              <span className="date-follow-label">{strings?.like || "Gefällt mir"}</span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
